import type {
  ArenaDaPlataforma,
  ContadoresDeRotas,
  NovaArenaDaPlataforma,
  NovoMembroDaPlataforma,
  PainelDeJobs,
  SaudeDetalhada,
  VinculoCriado,
} from '@arena/shared/schemas';
import { ErroDeApi, ErroDeRede } from './cliente.js';

/**
 * As chamadas da tela do operador (documento 20).
 *
 * NÃO passam por `api()`, e isso é de propósito. Aquele cliente põe o
 * token da SESSÃO de arena e, num 401, renova a sessão e manda para o
 * login. Aqui a credencial é outra — `Authorization: Operador <token>` —
 * e um 401 significa "credencial de operador recusada", não "sessão
 * expirada": deslogar a pessoa do aplicativo por causa disso seria o
 * bug clássico do 401 tratado no lugar errado.
 *
 * A credencial fica no `sessionStorage`, não no `localStorage` onde mora
 * a sessão: ela enxerga a fila de TODAS as arenas, e fechar a aba é o
 * bastante para esquecê-la.
 */

const BASE = (import.meta.env['VITE_API_URL'] as string | undefined)?.replace(/\/$/, '') ?? '/api';

const CHAVE = 'arena.operador';

export const credencialDeOperador = {
  ler(): string | null {
    try {
      return sessionStorage.getItem(CHAVE);
    } catch {
      return null;
    }
  },
  gravar(token: string): void {
    sessionStorage.setItem(CHAVE, token);
  },
  limpar(): void {
    sessionStorage.removeItem(CHAVE);
  },
};

/** GET sem corpo; POST com JSON quando há corpo. */
async function pedir<T>(caminho: string, token: string, envio?: unknown): Promise<T> {
  let resposta: Response;
  try {
    resposta = await fetch(`${BASE}${caminho}`, {
      method: envio === undefined ? 'GET' : 'POST',
      headers: {
        authorization: `Operador ${token}`,
        ...(envio === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(envio === undefined ? {} : { body: JSON.stringify(envio) }),
      cache: 'no-store',
    });
  } catch {
    throw new ErroDeRede();
  }

  const corpo = (await resposta.json().catch(() => ({}))) as Record<string, unknown>;
  // O 503 da saúde detalhada é RESPOSTA, com o corpo inteiro: é assim que
  // ela diz "há algo pedindo ação". Só é erro quando não traz a saúde.
  if (resposta.ok || (resposta.status === 503 && Array.isArray(corpo['alertas']))) {
    return corpo as T;
  }
  throw new ErroDeApi(
    resposta.status,
    typeof corpo['erro'] === 'string' ? corpo['erro'] : 'Não consegui completar esta operação.',
    corpo['campos'] as Record<string, string[]> | undefined,
    typeof corpo['requestId'] === 'string' ? corpo['requestId'] : undefined,
  );
}

export const operacaoApi = {
  saude(token: string): Promise<SaudeDetalhada> {
    return pedir<SaudeDetalhada>('/health/detalhado', token);
  },

  jobs(token: string, horas: number): Promise<PainelDeJobs> {
    return pedir<PainelDeJobs>(`/operacao/jobs?horas=${horas}`, token);
  },

  rotas(token: string, minutos: number): Promise<ContadoresDeRotas> {
    return pedir<ContadoresDeRotas>(`/operacao/rotas?minutos=${minutos}`, token);
  },
};

/**
 * O painel da plataforma: criar arena com dono e vincular pessoas. A
 * mesma credencial do operador, a mesma porta — ver PaginaDaPlataforma.
 */
export const plataformaApi = {
  arenas(token: string): Promise<ArenaDaPlataforma[]> {
    return pedir<ArenaDaPlataforma[]>('/plataforma/arenas', token);
  },

  criarArena(token: string, dados: NovaArenaDaPlataforma): Promise<VinculoCriado> {
    return pedir<VinculoCriado>('/plataforma/arenas', token, dados);
  },

  adicionarMembro(
    token: string,
    arenaId: string,
    dados: NovoMembroDaPlataforma,
  ): Promise<VinculoCriado> {
    return pedir<VinculoCriado>(`/plataforma/arenas/${arenaId}/membros`, token, dados);
  },
};
